import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { DispensePricingSearchFunction } from "models/functionModels/dispensePricing/DispensePricingSearchFunction";
import { DispensePricingApi } from "apis/DispensePricingApi";
import { DispensePricingDeleteFunction } from "models/functionModels/dispensePricing/DispensePricingDeleteFunction";
import { DispensePricingUpdateFunction } from "models/functionModels/dispensePricing/DispensePricingUpdateFunction";
import { ProductsSpecialPricingExtended } from "models/storeModels/ProductsSpecialPricing";

export const getDispensePricing = createAsyncThunk("dispansePricingSlice/getDispansePricing", async (params: DispensePricingSearchFunction) => {
    const response = await DispensePricingApi.SearchStores(params);
    return response.data;
});

export const updateDispensePricing = createAsyncThunk("dispansePricingSlice/updateDispansePricing", async (params: DispensePricingUpdateFunction) => {
    params = { ...params, clearAllExistingPricing: false };
    const response = await DispensePricingApi.SendToStores(params);
    return response.data;
});

export const deleteDispensePricing = createAsyncThunk("dispansePricingSlice/deleteDispansePricing", async (params: DispensePricingDeleteFunction) => {
    const response = await DispensePricingApi.DeleteFromStores(params);
    return response.data;
});

type DispensePricingStockState = {
    stockId: string;
    stockName: string;
    productsSpecialPricingID: number;
    privateFixedPrice: number | null;
    privateFixedPricePacks: number | null;
    privateFixedPriceStart: number | null;
    privateFixedPriceEnd: number | null;
    pbsFixedPrice: number | null;
    pbsFixedPricePacks: number | null;
    pbsFixedPriceStart: number | null;
    pbsFixedPriceEnd: number | null;
    clubFixedPrice: number | null;
    clubFixedPricePacks: number | null;
    clubFixedPriceStart: number | null;
    clubFixedPriceEnd: number | null;
};

const dispensePricingStockState: DispensePricingStockState = {
    stockId: "",
    stockName: "",
    productsSpecialPricingID: 0,
    privateFixedPrice: null,
    privateFixedPricePacks: null,
    privateFixedPriceStart: null,
    privateFixedPriceEnd: null,
    pbsFixedPrice: null,
    pbsFixedPricePacks: null,
    pbsFixedPriceStart: null,
    pbsFixedPriceEnd: null,
    clubFixedPrice: null,
    clubFixedPricePacks: null,
    clubFixedPriceStart: null,
    clubFixedPriceEnd: null,
};

type InitialStateType = DispensePricingStockState & {
    id: number;
    storeId: string;
    storeName: string;
    stockIndex: number;
    response: any;
    stores: any[];
    stockGroupsDropdown: any[];
    selectedStocks: string[];
    selectedRows: { position: number, id: number }[][];
    loading: boolean;
    stockPopup: boolean;
    masterStockPopup: boolean;
    clearAllExistingPricing: boolean;
    importWizardPopup: boolean;
    error: string;
    table: ProductsSpecialPricingExtended[];
    disableAutoSelectReset: boolean;
};

const initialState: InitialStateType = {
    id: 0,
    storeId: "",
    storeName: "",
    stockIndex: 0,
    ...dispensePricingStockState,
    response: {
        data: [],
    },
    stores: [],
    stockGroupsDropdown: [],
    selectedStocks: [],
    selectedRows: [],
    loading: false,
    stockPopup: false,
    masterStockPopup: false,
    clearAllExistingPricing: true,
    importWizardPopup: false,
    error: "",
    table: [],
    disableAutoSelectReset: false,
};

type ImportWizardStateType = {
    importWizardTable: any[];
    totalStocksToImport: number;
    processedStocks: number;
    isProcessing: boolean;
    fileSheets: any[];
    fileWorkBook: any[];
    fileLoaded: boolean;
    preview: boolean;
    canProceed: number;
    wizardPercentage: number;
    currentSheet: string;
    importBy: string;
};

const importWizardState : ImportWizardStateType = {
    importWizardTable: [],
    totalStocksToImport: 0,
    processedStocks: 0,
    isProcessing: false,
    fileSheets: [],
    fileWorkBook: [],
    fileLoaded: false,
    preview: true,
    canProceed:0,
    wizardPercentage: 10,
    currentSheet: "",
    importBy: "",
};

const dispensePricingSlice = createSlice({
    name: "dispensePricingSlice",
    initialState: {
        ...initialState,
        ...importWizardState,
    },
    reducers: {
        updateDispensePricingField: (state, action) => {
            return {
                ...state,
                [action.payload.key]: action.payload.value,
            };
        },
        addSelectedRows: (state, action) => {
            // @ts-ignore
            state.selectedRows[action.payload.index] = action.payload.rows;
        },
        addImportWizardTable: (state, action) => {
            // @ts-ignore
            state.importWizardTable = state.importWizardTable.concat(action.payload);
        },
        cleanDispensePricingDetails: (state) => {
            return {
                ...state,
                ...initialState,
            };
        },
        cleanDispensePricingStockState: (state) => {
            return {
                ...state,
                ...dispensePricingStockState,
            };
        },
        resetImportWizardState: (state) => {
            return {
                ...state,
                ...importWizardState,
            };
        },
        addAGGridSelectedStocks: (state, action) => {
            return {
                ...state,
                selectedStocks: action.payload
            };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getDispensePricing.pending, (state, action) => {
            state.loading = true;
            state.table = [];
            state.storeName = "";
            state.error = "";
        });
        builder.addCase(getDispensePricing.fulfilled, (state, action) => {
            state.loading = false;
            state.table = action.payload.stores[0]?.response?.data || [];
            state.storeName = action.payload.stores[0]?.storeName || "";
        });
        builder.addCase(getDispensePricing.rejected, (state, action) => {
            // state.table = [];
            // state.storeName = "";
            state.loading = false;
            state.error = action.error.message || "";
        });
    }
});

export const { addImportWizardTable, resetImportWizardState, cleanDispensePricingStockState, updateDispensePricingField,
    addSelectedRows, cleanDispensePricingDetails, addAGGridSelectedStocks } = dispensePricingSlice.actions;

export default dispensePricingSlice.reducer;
